import React, { useState, useEffect, useCallback } from "react";
import { authHeaders } from "../lib/auth";

/* ============================================================
   GENEROSITY(TM) WTR APP - HYDRATION PERFORMANCE (OURA ASSOCIATION)
   (renamed from Hydration Impact, v8 surgical pass, Jul 14 2026)
   ------------------------------------------------------------
   Renders the within-person association between the user's own
   Oura-INDEPENDENT hydration input (verified bottle intake by
   default, absorbed as fallback) and their Oura outcomes
   (Readiness, Sleep, Recovery), from paired days only.

   Honesty rules baked in (do not weaken):
   - Numbers appear ONLY at tier 'corroborated'. 'developing'
     shows direction only. 'hypothesis_only' shows the building
     state with days remaining. No fabricated composites, ever.
   - Every card carries the tier chip, the "from Oura" source
     label, and the association caveat.
   - The n=1 framing line always renders when any result shows.

   Wiring: reads GET /api/wtr/hydration/impact via authHeaders()
   (X-WTR-Auth session identity, same as the summary). Renders
   nothing at all when signed out or when the endpoint is
   unavailable, so it can never break the page around it.

   Usage in MyHydration.jsx (below the HYDRATION INSIGHTS cards):
     import HydrationImpact from "./HydrationImpact";
     ...
     <HydrationImpact />
   ============================================================ */

const API_BASE = "https://generosity-sales-engine-mvp-api.onrender.com";
const MIN_DAYS_FOR_DEVELOPING = 14; // mirrors the server gate; display only

const BRAND = {
  blue: "#51B0E6", gray: "#A6A8AB", card: "#F0F1F3", ink: "#2B2F33",
  green: "#4A8A6F", red: "#B84A4A",
};

const TIER_STYLES = {
  verified: { fg: "#3D7A5C", label: "verified" },
  corroborated: { fg: "#3D7AA6", label: "corroborated" },
  developing: { fg: "#A6792E", label: "developing" },
  hypothesis_only: { fg: "#7A7D80", label: "hypothesis only" },
};

function TierChip({ tier }) {
  // v8 surgical pass (Jul 14 2026): developing / hypothesis_only chips no
  // longer render. The honesty rules above are enforced by the DISPLAY
  // GATES (numbers only at corroborated; direction-only below), which are
  // untouched; the chip was labeling, not the gate.
  if (tier !== "verified" && tier !== "corroborated") return null;
  const t = TIER_STYLES[tier] || TIER_STYLES.hypothesis_only;
  return (
    <span style={{ background: "transparent", color: t.fg, border: "1.5px solid " + t.fg,
      borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.04em", textTransform: "lowercase", fontFamily: "'Montserrat', sans-serif",
      whiteSpace: "nowrap" }}>
      {t.label}
    </span>
  );
}

function DirectionNote({ direction }) {
  if (direction === "positive") {
    return <div className="gw-body" style={{ color: BRAND.blue, fontWeight: 600, marginTop: 8 }}>Trending positive</div>;
  }
  if (direction === "negative") {
    return <div className="gw-body" style={{ color: BRAND.red, fontWeight: 600, marginTop: 8 }}>Trending negative</div>;
  }
  return <div className="gw-body" style={{ color: BRAND.gray, fontWeight: 600, marginTop: 8 }}>No clear trend yet</div>;
}

function ImpactCard({ r }) {
  const corroborated = r.tier === "corroborated";
  const developing = r.tier === "developing";
  const building = !corroborated && !developing;
  const daysLeft = Math.max(0, MIN_DAYS_FOR_DEVELOPING - (r.nDays || 0));

  return (
    <div className="gw-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="gw-cardtitle">{r.metricLabel}</span>
        <TierChip tier={r.tier} />
      </div>
      <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 2 }}>{r.source}</div>

      {corroborated && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div className="gw-bignum" style={{ color: BRAND.gray }}>{r.lowAvg}</div>
              <div className="gw-micro" style={{ color: BRAND.gray }}>your lower-intake days</div>
            </div>
            <div className="gw-body" style={{ color: BRAND.gray, paddingBottom: 10 }}>vs</div>
            <div>
              <div className="gw-bignum" style={{ color: BRAND.blue }}>{r.wellAvg}</div>
              <div className="gw-micro" style={{ color: BRAND.gray }}>your well-hydrated days</div>
            </div>
            <div style={{ marginLeft: "auto", paddingBottom: 6 }}>
              <span className="gw-body" style={{ fontWeight: 700,
                color: r.delta >= 0 ? BRAND.blue : BRAND.red }}>
                {(r.delta >= 0 ? "+" : "") + r.delta}
              </span>
            </div>
          </div>
          <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 6 }}>
            {r.nDays} paired days. Range {r.ciLow} to {r.ciHigh}.
          </div>
        </div>
      )}

      {developing && (
        <div style={{ marginTop: 4 }}>
          <DirectionNote direction={r.direction} />
          <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 4 }}>
            {r.nDays} paired days so far. A number appears when the pattern is strong enough to stand behind.
          </div>
        </div>
      )}

      {building && (
        <div className="gw-body" style={{ color: BRAND.gray, marginTop: 10 }}>
          Building your hydration impact.
          {daysLeft > 0 ? " About " + daysLeft + " more paired days needed." : " Keep logging daily."}
        </div>
      )}

      {(corroborated || developing) && (
        <p className="gw-body" style={{ marginTop: 10 }}>{r.lever}</p>
      )}
      <div className="gw-micro" style={{ color: BRAND.gray, background: BRAND.card,
        borderRadius: 10, padding: "8px 10px", marginTop: 8 }}>
        {r.caveat}
      </div>
    </div>
  );
}

export default function HydrationImpact() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_BASE + "/api/wtr/hydration/impact?input=verified", {
        headers: authHeaders({ Accept: "application/json" }),
      });
      if (!res.ok) { setFailed(true); return; }
      const json = await res.json();
      if (!json.ok) { setFailed(true); return; }
      setData(json);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  // Signed out, endpoint unavailable, or error: render nothing, never break the page.
  if (failed || !data) return null;

  const results = Array.isArray(data.results) ? data.results : [];
  const anyShowing = results.some((r) => r.tier === "corroborated" || r.tier === "developing");

  return (
    <div style={{ marginTop: 22 }}>
      <div className="gw-eyebrow" style={{ color: BRAND.gray }}>HYDRATION PERFORMANCE</div>
      <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 4, lineHeight: 1.6 }}>
        How your own hydration pairs with your Oura scores.
        {data.pairedDays != null && data.pairedDays > 0
          ? " " + data.pairedDays + " paired days on record."
          : ""}
      </div>

      {!data.ready || results.length === 0 ? (
        <div className="gw-card">
          <div className="gw-cardtitle">Building your hydration impact</div>
          <p className="gw-body" style={{ marginTop: 6 }}>
            {data.message || "Wear your ring, drink from your WTR BTL, and open the app daily. Paired days start counting now."}
          </p>
        </div>
      ) : (
        <>
          {results.map((r) => (
            <ImpactCard key={r.metric} r={r} />
          ))}
          {anyShowing && (
            <div className="gw-micro" style={{ color: BRAND.gray, marginTop: 10, lineHeight: 1.6 }}>
              {data.framing || "Single-person association from your own data. A signal, not a proven cause."}
              {" "}
              {data.inputNote || ""}
            </div>
          )}
        </>
      )}
    </div>
  );
}
